<template>
  <Teleport to="body">
    <Transition name="dialog">
      <div 
        v-if="isOpen"
        class="fixed inset-0 z-[9998] flex items-center justify-center p-4"
      >
        <!-- Backdrop -->
        <div 
          class="absolute inset-0 bg-black bg-opacity-50"
          @click="handleCancel"
        ></div>

        <!-- Dialog -->
        <div class="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          <!-- Header -->
          <div class="px-6 py-4 border-b">
            <div class="flex items-center">
              <div 
                :class="[
                  'w-10 h-10 rounded-full flex items-center justify-center mr-3',
                  typeStyles[type].bg
                ]"
              >
                <svg 
                  v-if="type === 'warning'"
                  class="w-6 h-6"
                  :class="typeStyles[type].icon"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <svg 
                  v-else-if="type === 'error'"
                  class="w-6 h-6"
                  :class="typeStyles[type].icon"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <svg 
                  v-else-if="type === 'success'"
                  class="w-6 h-6"
                  :class="typeStyles[type].icon"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <svg 
                  v-else
                  class="w-6 h-6"
                  :class="typeStyles[type].icon"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">
                {{ title }}
              </h3>
            </div>
          </div>

          <!-- Body -->
          <div class="px-6 py-4">
            <p class="text-gray-600">{{ message }}</p>
            
            <!-- Additional content slot -->
            <div v-if="$slots.default" class="mt-4">
              <slot />
            </div>
          </div>

          <!-- Footer -->
          <div class="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end space-x-3">
            <button
              @click="handleCancel"
              class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {{ cancelText }}
            </button>
            <button
              @click="handleConfirm"
              :class="[
                'px-4 py-2 text-white rounded-lg transition-colors',
                typeStyles[type].button
              ]"
            >
              {{ confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'info'
  },
  title: {
    type: String,
    default: 'Conferma'
  },
  message: {
    type: String,
    default: 'Sei sicuro di voler procedere?'
  },
  confirmText: {
    type: String,
    default: 'Conferma'
  },
  cancelText: {
    type: String,
    default: 'Annulla'
  },
  confirmClass: {
    type: String,
    default: ''
  }
})

// Events
const emit = defineEmits(['confirm', 'cancel'])

const isOpen = computed(() => props.show)
const type = computed(() => props.type)
const title = computed(() => props.title)
const message = computed(() => props.message)
const confirmText = computed(() => props.confirmText)
const cancelText = computed(() => props.cancelText)

const typeStyles = {
  info: {
    bg: 'bg-blue-100',
    icon: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-700'
  },
  warning: {
    bg: 'bg-yellow-100',
    icon: 'text-yellow-600',
    button: 'bg-yellow-600 hover:bg-yellow-700'
  },
  error: {
    bg: 'bg-red-100',
    icon: 'text-red-600',
    button: 'bg-red-600 hover:bg-red-700'
  },
  success: {
    bg: 'bg-green-100',
    icon: 'text-green-600',
    button: 'bg-green-600 hover:bg-green-700'
  }
}

const handleConfirm = () => {
  emit('confirm')
}

const handleCancel = () => {
  emit('cancel')
}
</script>

<style scoped>
.dialog-enter-active,
.dialog-leave-active {
  transition: all 0.3s ease;
}

.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
}

.dialog-enter-from > div:last-child,
.dialog-leave-to > div:last-child {
  transform: scale(0.95);
}
</style>
